import { observable, action, computed } from 'mobx';
import { StoresApiSdk } from '~api/StoresApiSdk';
import { Store } from '~domain/entities/Store';
import { IGeoLocation } from '~domain/entities/UserLocation';
import { Config } from '~domain/entities/Config';
import {
    IStoresNearPointResult,
    IStoreByIdResult,
    IStoresByIdsResult,
    UnitDistance,
    IActiveFilters
} from '~api/Interfaces';
import { IShipMethod } from '~api/BOPISApiInterfaces';
import { StoreInventory } from '~domain/entities/StoreInventory';
import { query, serviceQueries } from '@estee/elc-service-bus';
import {
    BOPISApiSdk,
    IBOPISInventoryCoordsParams,
    IBOPISInventoryDoorIdsParams,
    IBOPISInventoryRequest,
    IBOPISInventoryZipCodeParams
} from '~api/BOPISApiSdk';
import { getBrandAndRegion } from '../../utils/BusinessContextUtils';
import { APIBOPISInventoryToAPIStoresArray } from '~api/BOPISToStores';
import { UrlFormatter } from '@estee/elc-formatters';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { InventoryCacheService } from '~api/InventoryCacheService';
import { IExpandedId } from '~use-cases/ExpandedStoreHoursIdUseCase';

export interface IStoreInventoryMap {
    [storeId: number]: StoreInventory;
}

export interface IDeliveryPrice {
    [key: number]: string;
}

export class StoresRepository {
    private storesApi: StoresApiSdk;
    private bopisApi: BOPISApiSdk;
    public urlFormatter: UrlFormatter;
    public inventoryCache: InventoryCacheService;

    @observable public isLoading: boolean;
    @observable public stores: Store[] = [];
    @observable public storeInventory: IStoreInventoryMap = {};
    @observable public map: Store[] = [];
    @observable public selectedStoreId: string = '';
    @observable public transactionStoreId: string = '';
    @observable public isAddToCalendarModalOpen: boolean;
    @observable public isErrorMessageOpen: boolean;
    @observable public isFilterModalOpen: boolean;
    @observable public expandedStoreHoursId: IExpandedId;
    @observable public configService: Config;
    @observable public BOPISError: Error;
    @observable public deliveryPrice: IDeliveryPrice = {};
    @observable public storeDetailsDistance: number;

    constructor(storesApi: StoresApiSdk, config: Config, bopisApi: BOPISApiSdk) {
        this.storesApi = storesApi;
        this.configService = config;
        this.bopisApi = bopisApi;
        this.urlFormatter = diContainer.get(serviceNames.urlFormatter);
        this.inventoryCache = diContainer.get(serviceNames.inventoryCacheService);
    }

    @computed get unitMeasure() {
        const { storesUnitDistance } = this.configService.config;

        return UnitDistance[storesUnitDistance.toLocaleUpperCase() as keyof typeof UnitDistance];
    }

    @computed
    get storeIds() {
        return this.stores.map((store) => store.id);
    }

    isAvailableInSelectedStore = (storeId: string) => {
        const selectedStore = this.stores.find((store) => store.id === storeId);

        return selectedStore?.productIsAvailable;
    };

    public loadStoresByLocation = async (
        location: IGeoLocation,
        filters: IActiveFilters,
        isServicesAvailable?: boolean,
        isInStorePickup?: boolean
    ) => {
        try {
            this.isLoading = true;
            const { isOAB, oabStoresSearchCount, storesSearchCount } = this.configService.config;

            const searchCount =
                isOAB && oabStoresSearchCount ? oabStoresSearchCount : storesSearchCount;

            const storesNearPointRequest = {
                coordinates: {
                    lat: location.lat,
                    lon: location.lng
                },
                size: searchCount,
                unit: this.unitMeasure,
                distance: this.configService.config.storesSearchRadius
            };

            const response = <IStoresNearPointResult>(
                await this.storesApi.getStoresNearPoint(storesNearPointRequest, filters, isOAB)
            );

            const responseItems = response?.storesNearPoint.items.filter((item) => {
                const hasServicesAvailable = isServicesAvailable ? item.services?.length : true;
                const hasInStorePickup = isInStorePickup ? item.in_store_pickup : true;

                return hasServicesAvailable && hasInStorePickup;
            });

            this.updateStores(
                responseItems.map((data) => Store.fromJson(data, this.configService))
            );
        } catch (err) {
            this.updateStores([]);
            this.setIsErrorMessageOpen(true);
        } finally {
            this.isLoading = false;
        }
    };

    public loadStoreById = async (storeId: string, location: IGeoLocation) => {
        const response = <IStoreByIdResult>await this.storesApi.getStoreBydId({
            id: storeId,
            coordinates: location
                ? {
                      lat: location.lat,
                      lon: location.lng
                  }
                : undefined,
            unit: this.unitMeasure
        });
        this.setStoreDetailsDistance(response.store.geographical.distance);
        await this.updateStores([Store.fromJson(response.store, this.configService)]);
    };

    @action
    public loadStoreByIds = async (storeIds: string[], userLocation?: IGeoLocation) => {
        try {
            const response = <IStoresByIdsResult>await this.storesApi.getStoreBydIds({
                ids: storeIds,
                coordinates: userLocation
                    ? {
                          lat: userLocation.lat,
                          lon: userLocation.lng
                      }
                    : undefined,
                unit: this.unitMeasure
            });
            this.stores = response
                ? response.storesByMultipleIds.items.map((store) =>
                      Store.fromJson(store, this.configService)
                  )
                : [];
        } catch (error) {
            this.stores = [];
        }
    };

    public getStoreFromCounterId = (counterId: string) => {
        return this.stores.find?.((store) => store.counterId === counterId);
    };

    @action
    public updateStores = (stores: Store[]) => {
        stores.forEach((store) => {
            store.storeUrl = this.urlFormatter.langPrefixUrl(store.storeUrl);
        });
        this.stores = stores || [];
    };

    @action
    public setStoreDetailsDistance = (distance: number) => {
        this.storeDetailsDistance = distance;
    };

    @action
    public setSelectedStoreId = (storeId: string) => {
        this.selectedStoreId = storeId;
    };

    @action
    public setTransactionStoreId = (storeId: string) => {
        this.transactionStoreId = storeId;
    };

    @action
    public resetSelectedStore = () => (this.selectedStoreId = '');

    @action
    public setIsAddToCalendarModalOpen = (isOpen: boolean) => {
        this.isAddToCalendarModalOpen = isOpen;
    };

    @action
    public setIsErrorMessageOpen = (isOpen: boolean) => {
        this.isErrorMessageOpen = isOpen;
    };

    @action
    public setIsFilterModalOpen = (isOpen: boolean) => {
        this.isFilterModalOpen = isOpen;
    };

    @action
    public setExpandedStoreHoursId = (expandedId: IExpandedId) => {
        this.expandedStoreHoursId = expandedId;
    };

    @action
    public loadStoreInventory = async (skuId: string, searchLocation: IGeoLocation) => {
        try {
            this.isLoading = true;
            const storeInventoryResponse = await query(serviceQueries.GET_STORE_INVENTORY, {
                skuId,
                geolocation: searchLocation,
                radius: this.configService.config.storesSearchRadius
            });

            this.storeInventory = Object.entries(
                storeInventoryResponse.door_inventory || {}
            ).reduce((acc: IStoreInventoryMap, [doorId, doorInventory]) => {
                const { available_qty, is_available, is_call, is_unavailable } =
                    doorInventory.skus_onhand[skuId];

                return {
                    ...acc,
                    [Number(doorId)]: new StoreInventory({
                        doorId: Number(doorId),
                        skuId,
                        availableQty: available_qty,
                        isAvailable: Boolean(is_available),
                        isCall: Boolean(is_call),
                        isUnavailable: Boolean(is_unavailable)
                    })
                };
            }, {});

            await this.loadStoreByIds(
                (storeInventoryResponse.doors || []).map(String),
                searchLocation
            );
        } finally {
            this.isLoading = false;
        }
    };

    @action
    public loadBOPISInventoryByZipCode = async (skuBaseId: number, zipcode: string) => {
        const { bopisRadius } = this.configService.config;
        const requestParams: IBOPISInventoryZipCodeParams = {
            radius: bopisRadius.toString(),
            mode: 'address',
            zip: zipcode
        };

        await this.loadBOPISInventory(skuBaseId, requestParams);
    };

    @action
    public loadBOPISInventoryByCoords = async (
        skuBaseId: number,
        searchLocation: IGeoLocation,
        filters?: IActiveFilters
    ) => {
        const { bopisRadius } = this.configService.config;
        const requestParams: IBOPISInventoryCoordsParams = {
            radius: bopisRadius.toString(),
            mode: 'coords',
            latitude: searchLocation?.lat.toString(),
            longitude: searchLocation?.lng.toString()
        };

        await this.loadBOPISInventory(skuBaseId, requestParams, filters);
    };

    @action
    public loadBOPISInventoryByDoorIds = async (skuBaseId: number, doorIds: string[]) => {
        const requestParams: IBOPISInventoryDoorIdsParams = {
            mode: 'door_id',
            door_ids: doorIds.sort()
        };

        await this.loadBOPISInventory(skuBaseId, requestParams);
    };

    @action
    public toOABOnlyStoresMode = () => {
        this.stores = this.stores.filter(
            (store) =>
                store.jdaStoreNumber &&
                store.jdaStoreNumber.length !== 0 &&
                store.canBookAppointments
        );
    };

    @action
    private loadBOPISInventory = async (
        skuBaseId: number,
        requestParams:
            | IBOPISInventoryDoorIdsParams
            | IBOPISInventoryCoordsParams
            | IBOPISInventoryZipCodeParams,
        filters?: IActiveFilters
    ) => {
        try {
            this.isLoading = true;

            const { brandId, regionId } = getBrandAndRegion();
            const { courierEnabled } = this.configService.config;

            let bopisInventoryResponse;
            //!!Temporary disabled inventory cache (enable when cart state change is syncronized)
            // const cachedInventory = await this.inventoryCache.getSSInventoryAsync(requestParams);
            // eslint-disable-next-line
            if (/*cachedInventory*/ false) {
                // bopisInventoryResponse = cachedInventory;
            } else {
                const bopisInventoryRequest: IBOPISInventoryRequest = {
                    brand: brandId,
                    region: regionId,
                    courier: courierEnabled ? 1 : 0,
                    include_cart: 1,
                    include_store_selection: 1,
                    sku_ids: [skuBaseId.toString()],
                    include_shipmethods: 1,
                    ...requestParams
                };

                bopisInventoryResponse = await this.bopisApi.getBOPISInventory(
                    bopisInventoryRequest
                );
                // this.inventoryCache.setSSInventory(requestParams, bopisInventoryResponse);
            }
            const responseMappedToStores = APIBOPISInventoryToAPIStoresArray(
                bopisInventoryResponse,
                skuBaseId,
                filters
            );
            const sortedAvailableStores = responseMappedToStores
                .filter((s) => s.productIsAvailable)
                .sort((a, b) => a.geographical.distance - b.geographical.distance);
            const sortedNotAvailableStores = responseMappedToStores
                .filter((s) => !s.productIsAvailable)
                .sort((a, b) => a.geographical.distance - b.geographical.distance);

            this.stores = [...sortedAvailableStores, ...sortedNotAvailableStores].map((store) =>
                Store.fromJson(store, this.configService)
            );
            if (this.isAvailableInSelectedStore(bopisInventoryResponse.bopis_store_number)) {
                this.setSelectedStoreId(bopisInventoryResponse.bopis_store_number);
            }
            if (bopisInventoryResponse.bopis_store_number) {
                this.setTransactionStoreId(bopisInventoryResponse.bopis_store_number);
            }
            this.setDeliveryMethodsPrice(bopisInventoryResponse.config.shipmethods);
        } catch (e) {
            this.BOPISError = e;
        } finally {
            this.isLoading = false;
        }
    };

    @action
    public setDeliveryMethodsPrice = (methods: IShipMethod[]) => {
        if (methods) {
            const shippingMethodsPrice: IDeliveryPrice = {};
            methods.forEach((method) => {
                const value = method.value;
                const price = method.price;
                shippingMethodsPrice[value] = price;
            });
            this.deliveryPrice = shippingMethodsPrice;
        }
    };
}
